import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { sessionService } from '../../services';
import useWebSocket from '../../hooks/useWebSocket';
import LiveStatsHeader from '../../components/session/monitor/LiveStatsHeader';
import NowPlayingCard from '../../components/session/monitor/NowPlayingCard';
import DeviceMixChart from '../../components/session/monitor/DeviceMixChart';
import LiveActivityFeed from '../../components/session/monitor/LiveActivityFeed';
import LiveParticipantList from '../../components/session/monitor/LiveParticipantList';

/**
 * Admin Session Monitor Page (super-admin only, read-only).
 *
 * Socket-driven: an initial GET /sessions/:id/live snapshot populates
 * everything, then every event we care about arrives over the
 * `admin:${sessionId}` socket room. We fall back to a 30s poll if the
 * socket disconnects, so the page never goes stale for long.
 */

const MAX_FEED_EVENTS = 200;

const eventFromSocket = (type, payload) => ({
  _id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ts: payload?.timestamp || new Date().toISOString(),
  type,
  participantId: payload?.participantId,
  participantName: payload?.participantName,
  activityId: payload?.activityId || payload?._id || payload?.id,
  activityTitle: payload?.title || payload?.name,
  activityType: payload?.type,
  subSessionId: payload?.subsessionId || payload?.subSessionId,
  subSessionName: payload?.name,
  metadata: {
    leaveReason: payload?.leaveReason,
    leaveDetails: payload?.leaveDetails,
    responseCount: payload?.responseCount,
    reason: payload?.reason,
  },
});

const AdminSessionMonitorPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('feed');
  const refetchTimerRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const res = await sessionService.getLiveMonitor(sessionId);
      if (res.success) setData(res.data);
    } catch (err) {
      console.error('[Monitor] fetchLive error:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchLive();
  }, [fetchLive]);

  // Socket subscription — joins the admin:${sessionId} room and listens for
  // every relevant event type. On each event we both prepend to the feed
  // (instant feedback) and re-fetch the snapshot (so aggregates like the
  // device-mix charts stay accurate). The re-fetch is debounced to 1s so
  // bursts of events don't hammer the API.
  const { socket, isConnected } = useWebSocket(sessionId, { disabled: !sessionId });

  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) return;
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      fetchLive();
    }, 1000);
  }, [fetchLive]);

  const prependEvent = useCallback((type, payload) => {
    setData((prev) => {
      if (!prev) return prev;
      const newEvent = eventFromSocket(type, payload);
      const events = [newEvent, ...(prev.events || [])].slice(0, MAX_FEED_EVENTS);
      return { ...prev, events };
    });
  }, []);

  useEffect(() => {
    if (!socket || !sessionId) return;

    socket.emit('join-admin-monitor', sessionId);

    const handlers = {
      'participant-joined':       (p) => { prependEvent('participant-joined', p); scheduleRefetch(); },
      'participant-reconnected':  (p) => { prependEvent('participant-reconnected', p); scheduleRefetch(); },
      'participant-left':         (p) => { prependEvent('participant-left', p); scheduleRefetch(); },
      'participant:removed':      (p) => { prependEvent('participant-removed', p); scheduleRefetch(); },
      'participant-backgrounded': (p) => { prependEvent('participant-backgrounded', p); },
      'participant-foregrounded': (p) => { prependEvent('participant-foregrounded', p); },
      'activity:launched':        (p) => { prependEvent('activity-launched', p); scheduleRefetch(); },
      'activity:closed':          (p) => { prependEvent('activity-closed', p); scheduleRefetch(); },
      'response-received':        (p) => { prependEvent('response-received', p); scheduleRefetch(); },
      'subsession:started':       (p) => { prependEvent('subsession-started', p); },
      'subsession:ended':         (p) => { prependEvent('subsession-ended', p); },
      'session:paused':           (p) => { prependEvent('session-paused', p); scheduleRefetch(); },
      'session:resumed':          (p) => { prependEvent('session-resumed', p); scheduleRefetch(); },
      'session:ended':            (p) => { prependEvent('session-ended', p); scheduleRefetch(); },
      'workbook:launched':        (p) => { prependEvent('workbook-launched', p); },
      'workbook:stopped':         (p) => { prependEvent('workbook-stopped', p); },
    };

    Object.entries(handlers).forEach(([evt, fn]) => socket.on(evt, fn));

    return () => {
      Object.entries(handlers).forEach(([evt, fn]) => socket.off(evt, fn));
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, [socket, sessionId, prependEvent, scheduleRefetch]);

  // Soft fallback: re-fetch every 30s if socket is disconnected — page never
  // goes stale for more than half a minute, even on flaky networks.
  useEffect(() => {
    if (isConnected) return;
    const id = setInterval(fetchLive, 30000);
    return () => clearInterval(id);
  }, [isConnected, fetchLive]);

  if (loading) {
    return (
      <DashboardLayout title="Monitor Session">
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-500">Loading live snapshot…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout title="Monitor Session">
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <p className="text-neutral-500">Session not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const { session, stats, deviceMix, browserMix, deviceTypeMix, leaveReasonMix, currentActivity, participants, events } = data;
  const isLive = session?.status === 'live';
  const isPaused = session?.status === 'paused';
  const isCompleted = session?.status === 'completed' || session?.status === 'expired';
  const totalParticipants = (stats?.joined || 0) + (stats?.left || 0) + (stats?.removed || 0);

  return (
    <DashboardLayout title={isLive ? 'Live Session Monitor' : 'Session Monitor'}>
      <div className="p-4 sm:p-6 space-y-5 pb-20 sm:pb-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-lg sm:text-2xl font-bold text-neutral-900">{session?.name || 'Session'}</h1>
              {isLive && <Badge variant="live" dot>Live</Badge>}
              {isPaused && <Badge variant="paused" dot>Paused</Badge>}
              {isCompleted && <Badge variant="completed">Completed</Badge>}
              <Badge variant="default">Read-Only</Badge>
              <span className={`inline-flex items-center gap-1 text-xs ${isConnected ? 'text-emerald-600' : 'text-amber-600'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                {isConnected ? 'Connected' : 'Reconnecting…'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-neutral-600">
              {session?.organization?.name || 'Organization'}
              {session?.trainer && ` · ${session.trainer.firstName || ''} ${session.trainer.lastName || ''}`.trim()}
              {session?.code && ` · Code: ${session.code}`}
              {session?.startTime && ` · Started ${new Date(session.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/sessions/${sessionId}`)}>
              Session Details
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/sessions')}>
              Back
            </Button>
          </div>
        </div>

        {/* Status banner */}
        {isLive && (
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded-lg flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <p className="text-sm text-emerald-900">Session is live — monitoring in real time.</p>
          </div>
        )}
        {isPaused && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-lg">
            <p className="text-sm text-amber-900">Session is paused by trainer.</p>
          </div>
        )}
        {isCompleted && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-lg">
            <p className="text-sm text-blue-900">Session has ended — viewing final state.</p>
          </div>
        )}

        <LiveStatsHeader stats={stats} />

        {/* Now Playing + device mixes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <NowPlayingCard activity={currentActivity} totalCount={totalParticipants} />
          <DeviceMixChart title="Device OS" mix={deviceMix} total={totalParticipants} />
          <DeviceMixChart title="Browser" mix={browserMix} total={totalParticipants} />
        </div>

        {/* Tabbed: Live Event Feed | Participants */}
        <Card padding="none">
          <div className="flex border-b border-neutral-100">
            <button
              type="button"
              onClick={() => setTab('feed')}
              className={`px-5 py-3 text-sm font-medium transition-colors ${tab === 'feed' ? 'text-primary-600 border-b-2 border-primary-500 -mb-px' : 'text-neutral-500 hover:text-neutral-700'}`}
            >
              Live Event Feed <span className="text-neutral-400 ml-1">({events?.length || 0})</span>
            </button>
            <button
              type="button"
              onClick={() => setTab('participants')}
              className={`px-5 py-3 text-sm font-medium transition-colors ${tab === 'participants' ? 'text-primary-600 border-b-2 border-primary-500 -mb-px' : 'text-neutral-500 hover:text-neutral-700'}`}
            >
              Participants <span className="text-neutral-400 ml-1">({participants?.length || 0})</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            {tab === 'feed'
              ? <LiveActivityFeed events={events || []} participants={participants || []} embedded />
              : <LiveParticipantList participants={participants || []} embedded />
            }
          </div>
        </Card>

        {/* Leave reason / device type / quick insight */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Object.keys(leaveReasonMix || {}).length > 0 ? (
            <DeviceMixChart title="Leave Reasons" mix={leaveReasonMix} total={(stats?.left || 0) + (stats?.removed || 0)} />
          ) : (
            <Card padding="md">
              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Leave Reasons</h3>
              <p className="text-xs text-neutral-400 italic">No leaves yet — all participants still active.</p>
            </Card>
          )}
          <DeviceMixChart title="Device Type" mix={deviceTypeMix} total={totalParticipants} />
          <Card padding="md">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">Quick Insight</h3>
            <ul className="space-y-2 text-xs text-neutral-600">
              <li>• {totalParticipants} unique participants tracked this session.</li>
              <li>• {stats?.completedActivities || 0} of {stats?.totalActivities || 0} activities completed.</li>
              <li>• Avg engagement {stats?.avgEngagement || 0}% across active participants.</li>
              {Object.keys(leaveReasonMix || {}).length > 0 && (
                <li>• Most common leave reason: <strong>{Object.entries(leaveReasonMix).sort((a, b) => b[1] - a[1])[0][0]}</strong>.</li>
              )}
            </ul>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminSessionMonitorPage;
